import ViewportScaledElement from './viewport-scaled-element';
import Modal from './modal';

const settings = {
    dataAttributes: {
        src: 'data-zoom-src',
        srcMobile: 'data-zoom-src-mobile',
        clickmap: 'data-zoom-clickmap',
        additionalClasses: 'data-zoom-modal-classes',
        allowScroll: 'data-zoom-allow-scroll',
    },

    classNames: {
        container: 'image-zoom-modal',
        loaded: 'modal--loaded',
        modal: 'modal--image-zoom',
        allowScroll: 'modal--allow-scroll',
    },

    template: `<img class="modal--image-zoom--image" />
    <img class="modal--image-zoom--image-mobile" />`,
};

/** Buttons that launch a modal containing a larger version of an image */
export default class ImageZoomModal extends ViewportScaledElement {
    constructor(openModalButton) {
        super(openModalButton);

        this.openModalButton = openModalButton;

        // Determine the image to present in the modal
        const zoomedImageSrc = this.openModalButton
            .getAttribute(settings.dataAttributes.src);
        if (!zoomedImageSrc) {
            return;
        }

        // Check for an optional mobile-specific image
        const zoomedImageSrcMobile = this.openModalButton
            .getAttribute(settings.dataAttributes.srcMobile);

        // Check for an optional clickmap
        const clickmapSrc = this.openModalButton
            .getAttribute(settings.dataAttributes.clickmap);

        // Check for an optional "additional classes" attribute
        const additionalClasses = this.openModalButton
            .getAttribute(settings.dataAttributes.additionalClasses);

        // Check for an optional "allow scroll" attribute,
        // which alters the way this modal functions
        const allowScroll = this.openModalButton
            .hasAttribute(settings.dataAttributes.allowScroll);

        // Create the class name to apply to the modal
        let modalClassName = settings.classNames.modal;

        if (allowScroll) {
            modalClassName += ` ${settings.classNames.allowScroll}`;
        }

        if (additionalClasses) {
            modalClassName += ` ${additionalClasses}`;
        }

        // Setup open event
        const clickCallback = (event) => {
            event.preventDefault();

            Modal.show(
                settings.template,
                modalClassName,
                Modal.presentationTypes.scaleUp,
                Modal.presentationTypes.scaleDown,
                !allowScroll,
                false,
                {
                    preShow: function() {
                        const modal = this;

                        // Find the image and set its src attribute
                        const image = modal.content.querySelector('.modal--image-zoom--image');
                        if (image) {
                            image.src = zoomedImageSrc;
                        }

                        const mobileImage = modal.content.querySelector('.modal--image-zoom--image-mobile');
                        if (zoomedImageSrcMobile) {
                            mobileImage.src = zoomedImageSrcMobile;

                            // Add a "desktop-only" class to the base image
                            image.classList.add('desktop-only');
                        } else {
                            // Remove the mobile image element since none was sepecified
                            mobileImage.parentNode.removeChild(mobileImage);
                        }

                        // Detect when the image has loaded (only one will load
                        // since they're desktop / mobile per the CSS)
                        const loadCallback = () => {
                            if (timeout) {
                                clearTimeout(timeout);
                                timeout = null;
                            }

                            // Add "loaded" class after a (very) slight delay -
                            // we're trying to give the layout time to size to
                            // the image before the modal becomes visible, as
                            // otherwise it would briefly appear too small
                            setTimeout(
                                () => {
                                    modal.container
                                        .classList
                                        .add(settings.classNames.loaded);

                                    // Adjust content height in case
                                    // scrolling is needed
                                    if (allowScroll) {
                                        modal.adjustContentHeight();
                                    }

                                    // If we opened due to a keyboard event, set focus on the close button
                                    if (event instanceof KeyboardEvent) {
                                        modal.closeButton.focus();
                                    }
                                },
                                10,
                            );
                        };

                        let timeout = setTimeout(
                            () => {
                                timeout = null;
                                loadCallback();
                            },
                            500,
                        );

                        image.onload = loadCallback;

                        // If a clickmap was specified, load it
                        if (clickmapSrc) {
                            loadClickmap(
                                image,
                                clickmapSrc,
                            );
                        }
                    },

                    postClose: function() {
                        // If we opened due to a keyboard event, set focus on the button
                        // that caused the modal to open (ideally this would be triggered
                        // based on a keyboard event when the modal is closed)
                        if (event instanceof KeyboardEvent) {
                            openModalButton.focus();
                        }
                    },
                },
                event,
            );
        };

        this.openModalButton.addEventListener(
            'keydown',
            (event) => {
                if (event.key != 'Enter') {
                    return;
                }

                clickCallback(event);
            });

        this.openModalButton.addEventListener('click', clickCallback);
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .getElementsByClassName(settings.classNames.container);

        for (let i=0; i<elements.length; i++) {
            new ImageZoomModal(elements[i]);
        }
    }
}

/*
    Internal functions
*/

function loadClickmap(
    image,
    clickmapSrc,
) {
    const request = new XMLHttpRequest();
    request.open('GET', clickmapSrc, true);

    // Success callback
    request.onload = () => {
        if (request.status != 200) {
            return;
        }

        // Add the SVG to the DOM
        let svg = document.createElement('div');
        svg.innerHTML = request.responseText;
        svg = svg.querySelector('svg');

        // Set up click events
        const svgLinks = svg.querySelectorAll('a');
        for (let i=0; i< svgLinks.length; i++) {
            const svgLink = svgLinks[i];
            const href = svgLink.getAttribute('xlink:href');
            if (!href) {
                continue;
            }

            // This doesn't seem to work unfortunately
            svgLink.tabIndex = 0;

            svgLink.addEventListener('click', (event) => {
                event.preventDefault();
                window.open(href);
            });
        }

        image.parentNode.appendChild(svg);
    };

    // Initiate the request
    request.send();
}
